import React, {
  useEffect,
  useState,
} from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import Style from './style';
import {
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Typography,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  FiberManualRecord as FiberManualRecordIcon,
  Save,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import logo from '../CardView/logo';
import { Interfaces, } from '../../../config';
import { useDSCalls, } from '../../../hooks';
import LoadingComponent from '../../Loading';
import {
  StyledButton,
  StyledTextField,
} from '../../Reusable';

type Params = {
  oldName: string;
};

function PostgreSQL() {
  const classes = Style();
  const location = useLocation();
  const navigate = useNavigate();
  const [formState, setFormState,] = useState<Interfaces.JDBCDataSource>({
    id: '',
    name: '',
    type: 'JDBC',
    uri: 'jdbc:postgresql://${host}:${port}',
    attributes: null,
    databaseName: '',
    databaseHost: '',
    databasePort: '5432',
    databaseTls: false,
    description: '',
    driverClassName: 'org.postgresql.Driver',
    driverFilePath: '',
    driverFileType: '',
    driverName: 'postgres',
    password: '',
    username: '',
  });
  const [showPassword, setShowPassword,] = React.useState<boolean>(false);
  const { oldName, } = useParams<Params>();
  const [isInEditMode, setIsInEditMode,] = useState(false);

  const { loading: loadingDS,
    editDataSource,
    addDataSource,
    testDataSource,
    dataSourceToEdit: dataSourceToEditHook,
    loadingEdit,
    errorEdit,
    loadingTest,
    errorTest,
    uploadDriverAPI,
    fetchDataSource,
    testNewDataSource,
    testExistingDataSource,
    setTestDataSource, } = useDSCalls();

  const dataSourceToEdit = dataSourceToEditHook as Interfaces.JDBCDataSource | null;
  const [loadingDriver, setLoadingDriver,] = useState(false);

  useEffect(() => {
    setTestDataSource(null);
    const editMode = location.pathname.includes('/edit');
    setIsInEditMode(editMode);
    if (editMode && dataSourceToEdit == null && oldName) {
      fetchDataSource(oldName);
    }
    if (editMode && dataSourceToEdit != null) {
      setFormState({
        ...formState,
        id: dataSourceToEdit?.id,
        uri: dataSourceToEdit?.uri,
        name: dataSourceToEdit?.name,
        description: dataSourceToEdit?.description,
        type: dataSourceToEdit?.type,
        driverName: dataSourceToEdit?.driverName,
        driverClassName: dataSourceToEdit?.driverClassName,
        driverFilePath: dataSourceToEdit?.driverFilePath,
        driverFileType: dataSourceToEdit?.driverFileType,
        databaseHost: dataSourceToEdit?.databaseHost,
        databaseName: dataSourceToEdit?.databaseName,
        databasePort: dataSourceToEdit?.databasePort,
        databaseTls: dataSourceToEdit?.databaseTls,
        username: dataSourceToEdit?.username,
        password: null,
      });
    }
  }, [dataSourceToEdit,]);

  const createUri = (name: string, dbName: string, host: string, port: string) => {
    name = 'postgresql:';
    const uri = `jdbc:${name}//${host}:${port}`;
    return `${uri}/${dbName}`;
  };

  const setUri = (name: string, value: string) => {
    switch (name) {
    case 'driverName':
      return createUri(value, formState.databaseName, formState.databaseHost, formState.databasePort);
    case 'databaseName':
      return createUri(formState.driverName, value, formState.databaseHost, formState.databasePort);
    case 'databaseHost':
      return createUri(formState.driverName, formState.databaseName, value, formState.databasePort);
    case 'databasePort':
      return createUri(formState.driverName, formState.databaseName, formState.databaseHost, value);
    case 'uri':
      return value;
    default:
      return formState.uri;
    }
  };

  const handleChange = () => (event: React.ChangeEvent<HTMLInputElement>) => {
    const name = event.target.name;
    const value = event.target.value;
    setFormState({
      ...formState,
      [name]: value,
      uri: setUri(name, value),
    });
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const onTestConnection = () => {
    isInEditMode ? testExistingDataSource(formState) : testNewDataSource(formState);
  };

  const driverUpload = (files: any) => {
    setLoadingDriver(true);
    if (files.length > 0) {
      uploadDriverAPI(files[0], formState.driverClassName, (driverFilePath) => {
        setFormState({
          ...formState,
          driverFilePath,
        });
        setLoadingDriver(false);
      });
      return;
    }
    setLoadingDriver(false);
  };

  const postSubmitAction = () => {navigate('/data-sources');};

  const handleSubmit = (event: any) => {
    event.preventDefault();
    if (location.pathname.includes('/edit') && oldName) {
      editDataSource(formState, oldName, postSubmitAction);
      return;
    }
    addDataSource(formState, postSubmitAction);
  };

  console.log(testDataSource);

  const handleTestSuccess = () => {
    if (loadingTest) {
      return <CircularProgress
        color="inherit"
        style={{
          width: 11,
          height: 11,
        }}
      />;
    }
    if (testDataSource && !errorTest) {
      return <FiberManualRecordIcon style={{ fill: 'green', }} />;
    } else if (
      !testDataSource &&
      !errorTest
    ) {
      return <FiberManualRecordIcon style={{ fill: 'grey', }} />;
    } else if (
      errorTest
    ) {
      return <FiberManualRecordIcon style={{ fill: 'red', }} />;
    }
  };

  if (loadingEdit) {
    return <LoadingComponent message={'Loading data source'}/>;
  }

  if (errorEdit) {
    return (
      <p>{errorEdit}</p>
    );
  }

  if (isInEditMode && !dataSourceToEdit) {
    return (
      <p>No data source was selected for edit mode</p>
    );
  }

  return (
    <div className={classes.root}>
      <Grid container justifyContent='center' >
        <img alt="jdbc" src={logo.images.postgresql} className={classes.img} />
      </Grid>
      <Grid container justifyContent='center' spacing={3}>
        <form name="addJdbcForm" onSubmit={handleSubmit} style={{ width: '78%', }}>
          <Grid item>
            <StyledTextField
              id="name"
              label="Connection Name"
              variant="outlined"
              name="name"
              onChange={handleChange()}
              fullWidth
              size="small"
              value={formState.name}
              required
            />
            <StyledTextField
              id="description"
              label="Description"
              multiline
              rows={4}
              variant="outlined"
              name="description"
              onChange={handleChange()}
              fullWidth
              value={formState.description}
              size="small"
            />
            <Grid container spacing={3}>
              <Grid item xs={10}>
                <StyledTextField
                  id="database.host"
                  label="Hostname"
                  variant="outlined"
                  name="databaseHost"
                  onChange={handleChange()}
                  fullWidth
                  size="small"
                  value={formState.databaseHost}
                  required
                />
              </Grid>
              <Grid item xs={2}>
                <StyledTextField
                  id="database.port"
                  label="Port"
                  variant="outlined"
                  name="databasePort"
                  onChange={handleChange()}
                  fullWidth
                  size="small"
                  value={formState.databasePort}
                  required
                />
              </Grid>
            </Grid>
            <StyledTextField
              id="database.name"
              label="Database name"
              variant="outlined"
              name="databaseName"
              onChange={handleChange()}
              fullWidth
              size="small"
              value={formState.databaseName}
              required
            />
            <StyledTextField
              id="credentials.username"
              label="Username"
              variant="outlined"
              name="username"
              onChange={handleChange()}
              fullWidth
              size="small"
              value={formState.username}
            />
            <StyledTextField
              type={showPassword ? 'text' : 'password'}
              value={formState.password}
              placeholder={formState.password === null ? '(Password protected)' : 'Password'}
              onFocus={() => setFormState({
                ...formState,
                password: '',
              })}
              fullWidth
              size={'small'}
              label={formState.password === null ? '(Password protected)' : 'Password'}
              onChange={handleChange()}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              name="password"
            />
            <Grid container spacing={3}>
              <Grid item xs={10}>
                <StyledTextField
                  id="outlined-basic"
                  label="JDBC Driver (Class Name)"
                  variant="outlined"
                  name="driverClassName"
                  onChange={handleChange()}
                  fullWidth
                  size="small"
                  value={formState.driverClassName}
                  required
                />
              </Grid>
              <Grid item xs={2}>
                <input
                  accept=".jar"
                  className={classes.input}
                  id="input-file"
                  onChange={(e: any) => {
                    driverUpload(e.target.files);
                    e.target.value = '';
                  }}
                  type="file"
                  disabled={!formState.driverClassName || formState.driverClassName === ''}
                />
                <label htmlFor="input-file">
                  <StyledButton
                    variant="contained"
                    color="primary"
                    component={'span'}
                    startIcon={
                      loadingDriver ?
                        <CircularProgress
                          color="inherit"
                          style={{
                            width: 16,
                            height: 11,
                          }} />
                        : <CloudUploadIcon />
                    }
                    size="large"
                    fullWidth
                    disabled={!formState.driverClassName || formState.driverClassName === ''}
                  >
                    Upload
                  </StyledButton>
                </label>
              </Grid>
            </Grid>
            <Grid container spacing={3}>
              <Grid item xs={10}>
                <StyledTextField
                  id="driver.uri"
                  placeholder="jdbc:sqlserver://{host}[:{port}][;databaseName={database}]"
                  label="JDBC Connection String"
                  variant="outlined"
                  name="uri"
                  onChange={handleChange()}
                  fullWidth
                  size="small"
                  value={formState.uri}
                  required
                />
              </Grid>
              <Grid item xs={2}>
                <StyledButton
                  variant="contained"
                  color="primary"
                  startIcon={handleTestSuccess()}
                  size="large"
                  fullWidth
                  onClick={() => {
                    onTestConnection();
                  }}
                >
                  Test
                </StyledButton>
              </Grid>
            </Grid>
            {errorTest && (
              <Typography variant="subtitle2" style={{ color: 'red', }}>
                {errorTest}
              </Typography>
            )}

            <StyledButton
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              style={{ marginLeft: 0, }}
              disabled={loadingDriver || loadingDriver}
              startIcon={(loadingDS || loadingDriver) ?
                <CircularProgress
                  color="inherit"
                  style={{
                    width: 16,
                    height: 11,
                  }} />
                :
                <Save />}
            >
              Save
            </StyledButton>
            <StyledButton
              variant="outlined"
              size="large"
              onClick={() => {
                navigate(-1);
              }}
            >
              Cancel
            </StyledButton>
          </Grid>
        </form>
      </Grid>
    </div>
  );
}

export default PostgreSQL;
